/**
 * How many days of Yom Tov a person keeps — which is not a fact about where
 * they are.
 *
 * Eretz Yisrael keeps one day and the diaspora keeps two, so an address answers
 * it for most people most of the time. It does not answer it for the people
 * most likely to be caught out: a bochur learning in Yerushalayim for the year
 * keeps two days there, a visitor from Israel keeps one here, and both have a
 * stated address that says the opposite. Derived from geography, either of them
 * is sent to on a day they are keeping as Yom Tov.
 *
 * So the platform ASKS, the way it already asks a shul (`shul_profile.inIsrael`
 * is a stored column, not a guess). Unanswered means "follow where I am", which
 * is the derivation and therefore changes nothing for anybody who has not said.
 * There is no third value spelling that out: null is the whole of it, because
 * two ways to say one thing is how the two come to disagree.
 */

import { isIsrael } from './countries'
import { isIsraelZone } from './zone-place'

export const YOM_TOV_MINHAGIM = ['one_day', 'two_days'] as const

export type YomTovMinhag = (typeof YOM_TOV_MINHAGIM)[number]

/**
 * Whether this person keeps one day, given their own answer and where they are.
 *
 * The stated answer wins outright. Failing one, the COUNTRY decides where it is
 * known — a country is somebody's own answer about where they are, while a zone
 * is their browser's and is wrong for anybody reporting a zone they are not in.
 */
export const keepsOneDay = (
  minhag: string | null | undefined,
  where: { country?: string | null; timeZoneId?: string | null },
): boolean => {
  if (minhag === 'one_day') return true
  if (minhag === 'two_days') return false
  if (where.country) return isIsrael(where.country)
  return where.timeZoneId ? isIsraelZone(where.timeZoneId) : false
}
